import { useEffect, useRef } from "react";

import {
  FEATURE_DATA_PROPERTY,
  FEATURE_TYPE_PROPERTY,
} from "@app/component/map/FeaturedMap/context/consts";
import useMap from "@app/component/map/FeaturedMap/context/useMap";
import { FeatureType } from "@app/component/map/FeaturedMap/types";
import useMapDataEventHandler from "@app/util/hook/map/useMapDataEventHandler";
import shallowComparison from "@app/util/shallowComparison";

import usePinYield from "./utils/usePinYield";

export interface FeatureProps<T> {
  geometryGetter?: (
    item: T
  ) => Exclude<google.maps.Data.FeatureOptions["geometry"], undefined> | undefined;
  idGetter: (item: T, type?: string) => number | string;
  isDifferent?: (a: T, b: T) => boolean;
  items: Iterable<T>;
  onClick?: (item: T, event: google.maps.Data.MouseEvent) => void;
  onDoubleClick?: (item: T, event: google.maps.Data.MouseEvent) => void;
  onMouseDown?: (item: T, event: google.maps.Data.MouseEvent) => void;
  onMouseLeave?: (item: T, event: google.maps.Data.MouseEvent) => void;
  onMouseMove?: (item: T, event: google.maps.Data.MouseEvent) => void;
  onMouseOver?: (item: T, event: google.maps.Data.MouseEvent) => void;
  onMouseUp?: (item: T, event: google.maps.Data.MouseEvent) => void;
  onRightClick?: (item: T, event: google.maps.Data.MouseEvent) => void;
  styleGetter?: (item: T) => google.maps.Data.StyleOptions;
  type: FeatureType;
  // For clickable polygons, which swallow events of the deck pins under them. Markers stay on top.
  yieldToPins?: boolean;
}

const Feature = <T>({
  geometryGetter,
  idGetter,
  isDifferent = shallowComparison,
  items,
  onClick,
  onDoubleClick,
  onMouseDown,
  onMouseLeave,
  onMouseMove,
  onMouseOver,
  onMouseUp,
  onRightClick,
  styleGetter,
  type,
  yieldToPins = false,
}: FeatureProps<T>) => {
  const { map, registerStylingFunction, unregisterStylingFunction } = useMap();

  // useEffect to update style function for the feature of this type
  useEffect(() => {
    // loggingService.debug(`useEffect for styling is called for Feature with type "${type}"`);
    if (!styleGetter) {
      return;
    }
    registerStylingFunction(type, (feature: google.maps.Data.Feature) => {
      const data = feature.getProperty(FEATURE_DATA_PROPERTY) as T;
      return data ? styleGetter(data) : {};
    });
  }, [registerStylingFunction, styleGetter, type]);

  // only call unregister when component is unmounted or type changed
  useEffect(
    () => () => {
      unregisterStylingFunction(type);
    },
    [map, type, unregisterStylingFunction]
  );

  const previousIdGetter = useRef(idGetter);
  const previousGeometryGetter = useRef(geometryGetter);

  // useEffect to update features for the given items
  useEffect(() => {
    // loggingService.debug(`useEffect for data is called for Feature with type "${type}"`);
    const typedIdToItem = new Map<number | string, T>();
    for (const item of items) {
      typedIdToItem.set(`${type}_${idGetter(item, type)}`, item);
    }

    // iterate over feature and try to update features
    // for older items
    map.data.forEach((feature) => {
      const featureType = feature.getProperty(FEATURE_TYPE_PROPERTY);
      if (featureType !== type) {
        return; // skip not ours features
      }
      if (previousIdGetter.current !== idGetter) {
        // typedId getter has changed, we can not rely on matching previous
        // items ids' and current items ids'. Hence, we delete all
        // old items and add them again.
        map.data.remove(feature);
        return;
      }

      const typedId = feature.getId();
      if (typedId !== undefined && typedIdToItem.has(typedId)) {
        const data = typedIdToItem.get(typedId)!;
        const oldData: T = feature.getProperty(FEATURE_DATA_PROPERTY) as T;
        if (isDifferent(oldData, data)) {
          // force re-draw
          feature.setProperty(FEATURE_DATA_PROPERTY, data);
          // update geometry too, since data changed
          if (geometryGetter) {
            const geometry = geometryGetter(data);
            if (geometry) {
              feature.setGeometry(geometry);
            }
          }
        } else if (previousGeometryGetter.current !== geometryGetter && geometryGetter) {
          // or at least update geometry if geometry getter has changed
          const geometry = geometryGetter(data);
          if (geometry) {
            feature.setGeometry(geometry);
          }
        }
        typedIdToItem.delete(typedId); // delete from the map to indicate that this item is already added in maps data
      } else {
        // if map feature no longer in the items array, delete it
        map.data.remove(feature);
      }
    });

    previousIdGetter.current = idGetter;
    previousGeometryGetter.current = geometryGetter;

    // now let's add new items (all existing items were deleted in a for-loop above)
    typedIdToItem.forEach((item, typedId) => {
      try {
        map.data.add({
          id: typedId,
          geometry: geometryGetter?.(item),
          properties: {
            [FEATURE_DATA_PROPERTY]: item,
            [FEATURE_TYPE_PROPERTY]: type,
          },
        });
      } catch (e) {
        console.error("feature fail", type, item, e);
      }
    });
  }, [geometryGetter, idGetter, isDifferent, items, map, type]);

  // remove all our features when component is unmounted
  useEffect(
    () => () =>
      map.data.forEach((feature) => {
        if (feature.getProperty(FEATURE_TYPE_PROPERTY) === type) {
          map.data.remove(feature);
        }
      }),
    [map, type]
  );

  const yieldToPin = usePinYield<T>(map, yieldToPins, { onMouseLeave, onMouseOver });
  const isOurs = (event: google.maps.Data.MouseEvent) =>
    event.feature.getProperty(FEATURE_TYPE_PROPERTY) === type;
  const getData = (event: google.maps.Data.MouseEvent) =>
    event.feature.getProperty(FEATURE_DATA_PROPERTY) as T;
  // Checked at event time, before the double click wait, so a pin click is not delayed twice. It
  // runs even without a handler: a clickable feature swallows pin events anyway.
  const yieldOn = (eventName: string) => ({
    intercept: (event: google.maps.Data.MouseEvent) =>
      isOurs(event) && yieldToPin(eventName, event, getData(event)),
  });
  const listen =
    (handler?: (item: T, event: google.maps.Data.MouseEvent) => void) =>
    (event: google.maps.Data.MouseEvent) => {
      const data = isOurs(event) ? getData(event) : undefined;
      if (handler && data) {
        handler(data, event);
      }
    };

  useMapDataEventHandler(map, "click", listen(onClick), yieldOn("click"));
  useMapDataEventHandler(map, "dblclick", listen(onDoubleClick), yieldOn("dblclick"));
  useMapDataEventHandler(map, "mousemove", listen(onMouseMove), yieldOn("mousemove"));
  useMapDataEventHandler(map, "mouseover", listen(onMouseOver), yieldOn("mouseover"));
  useMapDataEventHandler(map, "mouseout", listen(onMouseLeave), yieldOn("mouseout"));
  useMapDataEventHandler(map, "mousedown", listen(onMouseDown), yieldOn("mousedown"));
  useMapDataEventHandler(map, "mouseup", listen(onMouseUp), yieldOn("mouseup"));
  useMapDataEventHandler(map, "contextmenu", listen(onRightClick), yieldOn("contextmenu"));

  return null;
};

export default Feature;
